import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "../data/gear";

const links = [
  { to: "/", label: "Home", slug: "home" },
  { to: "/corporate-events", label: "Corporate Events", slug: "corporate-events" },
  { to: "/corporate-av", label: "AV Packages for Weddings", slug: "av-packages-weddings" },
  { to: "/av-gear-list", label: "AV Gear List", slug: "av-gear-list" },
  { to: "/beach-gear", label: "Beach Gear", slug: "beach-gear" },
];

const SCROLL_THRESHOLD = 30;

const navLinkClass = (active, scrolled) => {
  const base = "text-sm font-medium tracking-wide transition-colors";
  if (active) return `${base} text-accent`;
  if (scrolled) return `${base} text-primary hover:text-accent`;
  return `${base} text-white/90 hover:text-white`;
};

export const Navbar = ({ onQuote }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F9F7F2]/85 backdrop-blur-xl border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img
            src="/av30a-logo.jpg"
            alt="AV30A — Audio Visual Equipment Rentals, 30A Emerald Coast"
            className={`h-12 sm:h-14 w-auto object-contain rounded-lg transition-all duration-300 ${
              scrolled ? "" : "bg-white/95 px-2 py-1 shadow-md"
            }`}
          />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.slug}`}
              className={navLinkClass(pathname === l.to, scrolled)}
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="nav-quote-btn"
            onClick={onQuote}
            className="px-5 py-2.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-widest hover:bg-accent/90 transition-colors"
          >
            Request a Quote
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${scrolled ? "text-primary" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#F9F7F2] border-t border-border px-5 py-6 space-y-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.slug}`}
              className="block text-base font-medium text-primary"
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="mobile-nav-quote-btn"
            onClick={() => { setOpen(false); onQuote(); }}
            className="w-full px-5 py-3 rounded-full bg-accent text-accent-foreground text-sm font-semibold uppercase tracking-widest"
          >
            Request a Quote
          </button>
          <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-primary font-medium">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
};
